/**
 * NodeSummaryCards — one node, as six mini cards: cameras, recording & live, latest alerts,
 * AI models, groups, and the machine's own health.
 *
 * These started life inside the cloud dashboard's per-site row, which made them unreachable
 * from the product where a single node IS the whole installation. A local operator was left
 * with flat count tiles while the cloud operator got the donut, the alert list and the load
 * meters for the very same node — so the cards moved here and both products render them.
 *
 * Deliberately data-in, navigation-out. Each app fetches through its own transport (local hits
 * its services directly; cloud tunnels to the node) and each owns what a card click means —
 * cloud has to SELECT the node before routing, local just routes — so this component receives
 * an already-normalised `data` bundle plus a `links` map, and knows nothing about either.
 *
 * A card with no entry in `links` renders as a plain panel rather than a dead button: that is
 * how a permission the reader lacks is expressed. Its data still shows if they can read it.
 */
import type { ComponentType, ReactNode } from 'react';
import {
  ServerStackIcon,
  FilmIcon,
  CpuChipIcon,
  FolderIcon,
  VideoCameraIcon,
  BellAlertIcon,
  ClockIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import CameraStatusDonut, { type DonutSlice } from './CameraStatusDonut';

/**
 * Soft tinted background + solid text from a CSS-var colour.
 *
 * `bg-success/15` does NOT work here: the theme maps these tokens as full colour values
 * (`success: 'var(--success)'`), and Tailwind's opacity modifier can't decompose that into
 * channels — the rule silently produces nothing.
 */
const tint = (c: string) => ({
  backgroundColor: `color-mix(in srgb, ${c} 15%, transparent)`,
  color: c,
});

/**
 * Every card this component can draw.
 *
 * The first six are the original set, in the order they read: what exists → what it's doing →
 * what it found. The last two are MERGED cards, for surfaces with less room:
 *
 *   cameras-live   = `cameras` + `recording` in one tile (the ring, then what it's doing)
 *   models-groups  = `models` + `groups` in one tile (what the AI runs, and how cameras are filed)
 *   cameras-models = all four: the ring, what it's doing, and what the AI is pointed at. For a
 *                    dashboard where these tiles sit in the SAME grid as the analytics cards and
 *                    the estate's plumbing should read as one tile, not three.
 *
 * A merged card is not a different card — it is the same two answers on one tile, which is what
 * lets a dashboard trade six tiles for three without dropping anything.
 */
export type NodeSummaryCardKey =
  | 'cameras'
  | 'recording'
  | 'alerts'
  | 'models'
  | 'groups'
  | 'status'
  | 'cameras-live'
  | 'models-groups'
  | 'cameras-models';

/** The original six, unchanged — what a caller gets by not asking for anything else. */
const DEFAULT_CARDS: NodeSummaryCardKey[] = ['cameras', 'recording', 'alerts', 'models', 'groups', 'status'];

/**
 * Host metrics, in the cloud node-registry's field names — the local app maps its
 * `/monitoring/system` payload into this shape so one card serves both.
 *
 * Every field is optional: a card says "—" for what a node didn't report rather than printing
 * a confident zero.
 */
export interface NodeSummaryMetrics {
  cameraCount?: number;
  /** Live subscriptions on the node's media path. Omitted → the row is hidden, not zeroed. */
  activeStreams?: number;
  uptimeSeconds?: number;
  cpuPercent?: number;
  memoryPercent?: number;
  memoryUsedMB?: number;
  memoryTotalMB?: number;
  storageUsedGB?: number;
  storageTotalGB?: number;
  /** Any truthy value means "metrics have arrived" — used to stop the status skeleton. */
  timestamp?: any;
}

export interface NodeSummaryData {
  cameras: any[];
  groups: any[];
  /** `/models/assignments/all` payload: { models, camera_assignments, group_assignments }. */
  assignments: any;
  /** Latest alerts, newest first. */
  alerts: any[];
  metrics?: NodeSummaryMetrics;
  loading?: boolean;
}

export interface NodeSummaryCardsProps {
  data: NodeSummaryData;
  /** Destination per card. Omit a key to render that card as a non-interactive panel. */
  links?: Partial<Record<NodeSummaryCardKey, string>>;
  /**
   * How to follow a link. Cloud selects the node first (the destination is node-scoped and
   * would otherwise read whichever site was previously selected); local just navigates.
   */
  onNavigate?: (path: string) => void;
  /** Extra note under the health card — e.g. "worst of 3 machines" on a multi-machine node. */
  statusNote?: ReactNode;
  /**
   * Return the tiles WITHOUT a grid around them.
   *
   * For a dashboard that puts these cards in the same grid as its analytics cards: two grids
   * stacked look combined but align independently, so the last row of one and the first row of the
   * other sit on different lines. Handing the tiles over as bare children lets one grid own every
   * card and every row line up. `className` and the column logic are ignored in this mode — they
   * belong to the grid, and in this mode the caller owns it.
   */
  bare?: boolean;
  /**
   * Which cards to draw, in order. Defaults to the original six.
   *
   * The two dashboards pass the merged, shorter set — they show the AI analytics section above
   * this one, so their alerts card lives up there and their remaining tiles have room to
   * combine. The all-sites row keeps the default: one row per site is where the alert count is
   * the whole point, and there is no analytics section beside it to move it into.
   */
  cards?: NodeSummaryCardKey[];
  /**
   * Draw the chosen cards as COLUMNS OF ONE SHEET — hairline dividers between them, no frame
   * of their own, no frame around the set.
   *
   * For a titled section that already provides the sheet (the dashboards' "System Performance"
   * block). Three framed tiles inside a framed section is a box in a box, and it splits one
   * answer — what this machine has, what the AI is pointed at, how it is bearing it — into
   * three objects a reader has to gather back up. Dividers say "parts of one thing" where
   * borders say "three things".
   *
   * The caller owns the outer frame, so this renders no border, radius or padding of its own.
   */
  panel?: boolean;
  className?: string;
}

/** Camera status → bucket. Anything unrecognised is offline. */
const CAMERA_BUCKETS: Record<string, 'online' | 'connecting' | 'error'> = {
  streaming: 'online',
  recording: 'online',
  connecting: 'connecting',
  error: 'error',
};

export default function NodeSummaryCards({
  data,
  links = {},
  onNavigate,
  statusNote,
  cards = DEFAULT_CARDS,
  panel = false,
  bare = false,
  className = '',
}: NodeSummaryCardsProps) {
  const m = data.metrics || {};
  const cameras = data.cameras || [];
  const loading = !!data.loading;

  const go = (key: NodeSummaryCardKey) => {
    const path = links[key];
    if (!path || !onNavigate) return undefined;
    return () => onNavigate(path);
  };

  // ── Camera status buckets ──
  const camCounts = { online: 0, connecting: 0, error: 0, offline: 0 };
  for (const cam of cameras) camCounts[CAMERA_BUCKETS[cam.status] || 'offline'] += 1;
  // The ring's total is the sum of its own buckets, never metrics.cameraCount: if the camera
  // list failed to load, falling back to cameraCount would print "12 cameras" in the hole
  // above a legend that sums to 0.
  const cameraTotal = cameras.length;
  /** Node reports cameras but we couldn't read the list — say so, don't show zeros. */
  const cameraCountsUnavailable = cameras.length === 0 && num(m.cameraCount) > 0;

  const donutSlices: DonutSlice[] = [
    { key: 'online', label: 'Online', value: camCounts.online, color: 'var(--success)' },
    { key: 'connecting', label: 'Connecting', value: camCounts.connecting, color: 'var(--warning)' },
    { key: 'error', label: 'Error', value: camCounts.error, color: 'var(--destructive)' },
    { key: 'offline', label: 'Offline', value: camCounts.offline, color: 'var(--muted-foreground)' },
  ];

  // ── Recording / live ──
  // `is_recording` is the real signal: the backend derives it from a fresh 'in-progress' row in
  // `recording_segments`. Do NOT count `status === 'recording'` — `cameras.status` holds one
  // value and the pipeline only ever writes streaming/connecting/inactive/error to it, so a
  // recording camera reads 'streaming' there and the status check silently returns 0.
  const recordingCount = cameras.filter((c) => c.is_recording).length;
  const streamingCount = cameras.filter((c) => c.status === 'streaming').length;

  // ── AI models ──
  const a = data.assignments || {};
  const modelTotal = (a.models || []).length;
  const camAssignments = (a.camera_assignments || []).length;
  const groupAssignments = (a.group_assignments || []).length;
  /** Distinct models actually attached somewhere — "assigned" in the useful sense. */
  const modelsInUse = new Set(
    [...(a.camera_assignments || []), ...(a.group_assignments || [])].map((x: any) => x.model_id),
  ).size;

  // ── Groups ──
  const groups = data.groups || [];
  const groupNames = groups.slice(0, 3).map((g: any) => g.name).filter(Boolean);

  // ── Alerts ──
  const alerts = data.alerts || [];
  const unacked = alerts.filter((al) => !al.acknowledged && !al.acknowledged_by).length;

  /**
   * key → the tile, built lazily so an unrequested card costs nothing to render.
   *
   * The merged entries deliberately reuse the very same figures as their single counterparts
   * rather than recomputing anything: `cameras-live` is the donut followed by the recording
   * rows, `models-groups` is the two heroes side by side. That way a reader comparing the
   * all-sites row against a dashboard sees the same numbers, differently arranged.
   */
  /**
   * key → the tile, built lazily so an unrequested card costs nothing.
   *
   * The merged entries deliberately reuse the very same figures as their single counterparts
   * rather than recomputing anything, so a reader comparing the all-sites row against a
   * dashboard sees the same numbers, differently arranged.
   */
  const CARDS: Record<NodeSummaryCardKey, () => ReactNode> = {
    // ── Cameras: the status ring ──
    cameras: () => (
      <MiniCard flat={panel} key="cameras" title="Cameras" icon={VideoCameraIcon} onClick={go('cameras')} loading={loading}>
        {cameraCountsUnavailable ? (
          <p className="text-[11px] text-muted-foreground">
            This node reports {num(m.cameraCount)} camera(s), but the list couldn't be read.
          </p>
        ) : (
          <CameraStatusDonut slices={donutSlices} total={cameraTotal} />
        )}
      </MiniCard>
    ),

    // ── Recording / live ──
    recording: () => (
      <MiniCard flat={panel} key="recording" title="Recording & Live" icon={FilmIcon} onClick={go('recording')} loading={loading}>
        {/* Recording and Streaming are peers — neither is subordinate, so they get the same
            figure size rather than a hero + a small row. */}
        <HeroPair
          items={[
            { value: recordingCount, label: 'Recording', tone: 'var(--destructive)' },
            { value: streamingCount, label: 'Streaming', tone: 'var(--success)' },
          ]}
        />
        {m.activeStreams != null && (
          <div className="mt-2 space-y-1.5 border-t border-border pt-2">
            <Row label="Active streams" value={String(num(m.activeStreams))} />
          </div>
        )}
      </MiniCard>
    ),

    /**
     * ── MERGED: cameras + recording ──
     * The ring answers "what have I got and is it healthy"; the rows under it answer "what is it
     * doing right now". One tile, because on a dashboard those are one glance. Clicking follows
     * `links.cameras` — the camera list is the destination that covers both halves, and
     * Recordings has its own top-level nav entry.
     */
    'cameras-live': () => (
      <MiniCard flat={panel} key="cameras-live" title="Cameras" icon={VideoCameraIcon} onClick={go('cameras')} loading={loading}>
        {cameraCountsUnavailable ? (
          <p className="text-[11px] text-muted-foreground">
            This node reports {num(m.cameraCount)} camera(s), but the list couldn't be read.
          </p>
        ) : (
          <CameraStatusDonut slices={donutSlices} total={cameraTotal} />
        )}
        <div className="mt-2 space-y-1.5 border-t border-border pt-2">
          <Row label="Recording" value={String(recordingCount)} />
          <Row label="Streaming" value={String(streamingCount)} />
          {m.activeStreams != null && <Row label="Active streams" value={String(num(m.activeStreams))} />}
        </div>
      </MiniCard>
    ),

    // ── Latest alerts ──
    alerts: () => (
      <MiniCard
        flat={panel}
        key="alerts"
        title="Latest Alerts"
        icon={BellAlertIcon}
        badge={unacked > 0 ? `${unacked} new` : undefined}
        onClick={go('alerts')}
        loading={loading}
      >
        {alerts.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">No recent alerts.</p>
        ) : (
          <ul className="space-y-1.5">
            {alerts.slice(0, 4).map((al, i) => (
              <li key={al.id || i} className="flex items-start gap-1.5 text-[11px] leading-tight">
                <span
                  className="mt-1 h-1.5 w-1.5 flex-none rounded-full"
                  style={{ backgroundColor: severityColor(al.severity) }}
                  aria-hidden
                />
                <span className="min-w-0 flex-1 truncate text-foreground" title={al.event_type || al.title}>
                  {al.event_type || al.title || 'Alert'}
                </span>
                <span className="flex-none text-muted-foreground">{formatAlertTime(al)}</span>
              </li>
            ))}
          </ul>
        )}
      </MiniCard>
    ),

    // ── AI models ──
    models: () => (
      <MiniCard flat={panel} key="models" title="AI Models" icon={CpuChipIcon} onClick={go('models')} loading={loading}>
        <Hero value={modelTotal} caption={modelTotal === 1 ? 'model registered' : 'models registered'} />
        <div className="mt-2 space-y-1.5 border-t border-border pt-2">
          <Row label="Attached" value={String(modelsInUse)} />
          <Row label="Camera rules" value={String(camAssignments)} />
          <Row label="Group rules" value={String(groupAssignments)} />
        </div>
      </MiniCard>
    ),

    // ── Groups ──
    groups: () => (
      <MiniCard flat={panel} key="groups" title="Groups" icon={FolderIcon} onClick={go('groups')} loading={loading}>
        <Hero value={groups.length} caption={groups.length === 1 ? 'group' : 'groups'} />
        {groupNames.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-1 border-t border-border pt-2">
            {/* bg-card, not bg-muted: the chips sit on a recessed tile, so the card surface is
                what makes them read as raised in both themes (`--muted` is nearly identical to
                `--background` in light mode). */}
            {groupNames.map((n: string) => (
              <span
                key={n}
                className="max-w-full truncate rounded border border-border bg-card px-1.5 py-0.5 text-[10px] text-muted-foreground"
              >
                {n}
              </span>
            ))}
            {groups.length > groupNames.length && (
              <span className="rounded px-1.5 py-0.5 text-[10px] text-muted-foreground">
                +{groups.length - groupNames.length}
              </span>
            )}
          </div>
        ) : (
          <p className="mt-2 border-t border-border pt-2 text-[11px] text-muted-foreground">No groups defined</p>
        )}
      </MiniCard>
    ),

    /**
     * ── MERGED: models + groups ──
     * Two peers, so HeroPair rather than a hero and a footnote: what the AI runs, and how the
     * cameras are filed. The attachment counts stay because "3 models registered" says nothing
     * about whether any of them is actually pointed at a camera. Clicking follows
     * `links.models`, the busier of the two destinations.
     */
    'models-groups': () => (
      <MiniCard
        flat={panel}
        key="models-groups"
        title="AI Models & Groups"
        icon={CpuChipIcon}
        onClick={go('models') || go('groups')}
        loading={loading}
      >
        <HeroPair
          items={[
            { value: modelTotal, label: modelTotal === 1 ? 'Model' : 'Models' },
            { value: groups.length, label: groups.length === 1 ? 'Group' : 'Groups' },
          ]}
        />
        <div className="mt-2 space-y-1.5 border-t border-border pt-2">
          <Row label="Models attached" value={String(modelsInUse)} />
          <Row label="Camera / group rules" value={`${camAssignments} / ${groupAssignments}`} />
        </div>
      </MiniCard>
    ),

    /**
     * ── MERGED: cameras + recording + models + groups ──
     *
     * One answer to "what is this installation pointed at, and is it working": the ring says what
     * exists and its state, the first block what it is doing right now, the second what the AI is
     * attached to. Three tiles' worth of figures, unchanged — the same numbers the separate cards
     * show, arranged as one glance.
     *
     * Clicking follows `links.cameras`: the camera list is the destination that covers the
     * majority of the tile, and Recordings and Model Assignments both have their own nav entries.
     */
    'cameras-models': () => (
      <MiniCard flat={panel} key="cameras-models" title="Cameras & AI Models" icon={VideoCameraIcon} onClick={go('cameras')} loading={loading}>
        {cameraCountsUnavailable ? (
          <p className="text-[11px] text-muted-foreground">
            This node reports {num(m.cameraCount)} camera(s), but the list couldn't be read.
          </p>
        ) : (
          <CameraStatusDonut slices={donutSlices} total={cameraTotal} />
        )}
        <div className="mt-2 space-y-1.5 border-t border-border pt-2">
          <Row label="Recording" value={String(recordingCount)} />
          <Row label="Streaming" value={String(streamingCount)} />
          {m.activeStreams != null && <Row label="Active streams" value={String(num(m.activeStreams))} />}
        </div>
        <div className="mt-2 space-y-1.5 border-t border-border pt-2">
          {/* "attached / registered", because "3 models" says nothing about whether any of them is
              actually pointed at a camera. */}
          <Row label="AI models attached" value={`${modelsInUse} / ${modelTotal}`} />
          <Row label="Camera / group rules" value={`${camAssignments} / ${groupAssignments}`} />
          <Row label="Camera groups" value={String(groups.length)} />
        </div>
      </MiniCard>
    ),

    // ── Machine health ──
    status: () => (
      <MiniCard flat={panel} key="status" title="System Health" icon={ServerStackIcon} onClick={go('status')} loading={loading && !m.timestamp}>
        <div className="space-y-1.5">
          <Row label="Uptime" value={formatUptime(m.uptimeSeconds)} icon={ClockIcon} />
          <Meter label="CPU" percent={num(m.cpuPercent)} />
          <Meter
            label="Memory"
            percent={num(m.memoryPercent)}
            hint={m.memoryUsedMB ? `${gb(m.memoryUsedMB)} / ${gb(m.memoryTotalMB)}` : undefined}
          />
          <Meter
            label="Disk"
            percent={pct(m.storageUsedGB, m.storageTotalGB)}
            hint={m.storageTotalGB ? `${Math.round(num(m.storageUsedGB))} / ${Math.round(num(m.storageTotalGB))} GB` : undefined}
          />
          {statusNote && <p className="pt-0.5 text-[10px] text-muted-foreground">{statusNote}</p>}
        </div>
      </MiniCard>
    ),
  };

  /**
   * Columns follow the card COUNT, because a grid tuned for six leaves three tiles stretched
   * across a 2560px screen with nothing beside them. Six keeps the original breakpoints; a
   * shorter set tops out at its own length.
   */
  const columns =
    cards.length >= 6
      ? 'sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6'
      : cards.length >= 4
        ? 'sm:grid-cols-2 xl:grid-cols-4'
        : 'sm:grid-cols-2 lg:grid-cols-3';

  if (bare) return <>{cards.map((key) => CARDS[key]?.() ?? null)}</>;

  if (panel) {
    /* Stacked on a phone, side by side from `md` — with the divider swapping axis to match, so
       the hairline always runs BETWEEN the columns and never along the end of the row. */
    const panelCols =
      cards.length >= 4 ? 'md:grid-cols-2 xl:grid-cols-4' : cards.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2';
    return (
      <div
        className={`grid grid-cols-1 divide-y divide-border md:divide-y-0 md:divide-x ${panelCols} ${className}`}
      >
        {cards.map((key) => CARDS[key]?.() ?? null)}
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-1 gap-3 ${columns} ${className}`}>
      {cards.map((key) => CARDS[key]?.() ?? null)}
    </div>
  );
}

/* ─── Mini card chrome ─── */

/**
 * `group` + `group-hover:` is what lets the whole tile drive its own hover state — the arrow and
 * icon chip light up together rather than each needing its own handler. `min-h` equalises tiles
 * whose content is only 2–3 rows against the donut and the four meters, so the row isn't ragged.
 *
 * The chrome is shared with the analytics cards on purpose. The two now sit on SEPARATE titled
 * sheets — one for what the cameras saw, one for how the machine is bearing it — and the
 * headings are what distinguishes them; giving the tiles a heavier header or a different radius
 * as well would make the same distinction twice and read as two unrelated widgets on one page.
 * Sized for the taller of the two — the analytics tiles carry a figure, a chart, an axis and a
 * list — which is why the header is a touch larger and the padding a touch wider than a bare
 * count tile needs.
 *
 * The body is a flex column with `justify-between`, which is what makes a row of cards with
 * unequal content read evenly: a grid stretches every tile to the tallest in its row, and the
 * leftover height is distributed between the blocks rather than left in a heap under the last
 * one. Blocks that should soak it up instead of being pushed apart say so themselves — the
 * analytics charts flex, see `Plot` in AiAnalyticsCards.
 */
export function MiniCard({
  title,
  icon: Icon,
  children,
  onClick,
  badge,
  loading,
  compact = false,
  flat = false,
  className = '',
}: {
  title: string;
  icon: ComponentType<{ className?: string }>;
  children: ReactNode;
  onClick?: () => void;
  badge?: string;
  loading?: boolean;
  /**
   * Drop the floor height, for a card that SHARES a cell with another.
   *
   * `min-h` stops a lone two-row tile reading as filler beside the donut and the meters. A card
   * stacked with a sibling has the opposite problem: both must fit one cell, so each has to be
   * free to take only what its content needs.
   */
  compact?: boolean;
  /**
   * Drop the tile's own frame — no border, no recessed surface, no floor height.
   *
   * For a PANEL, where several of these are columns of one sheet divided by hairlines rather
   * than separate objects on a page (see NodeSummaryCards' `panel`). A framed tile inside a
   * framed sheet is a box in a box, and the reader counts four things where there are three.
   * Hover still responds — the column is still a link — it just tints instead of lifting,
   * because a column that rises off a shared sheet drags the dividers with it.
   */
  flat?: boolean;
  className?: string;
}) {
  const interactive = !!onClick;
  return (
    <div
      // stopPropagation so a tile click doesn't ALSO fire an enclosing row's own handler — the
      // tile's destination must win.
      onClick={interactive ? (e) => { e.stopPropagation(); onClick?.(); } : undefined}
      // Keyboard parity: a div with onClick is unreachable by keyboard otherwise.
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onClick?.();
              }
            }
          : undefined
      }
      // `bg-background` recesses the tile into the row's `bg-card` sheet, and it works in both
      // themes: the page surface is lighter than the card in light mode and darker in dark.
      // Hover lifts the tile TO the card surface, so it reads as picked up off the sheet.
      className={`group flex flex-col transition-all ${
        flat ? 'p-4' : `rounded-xl border border-border bg-background ${compact ? 'p-3' : 'min-h-[148px] p-3.5'}`
      } ${className} ${
        interactive
          ? flat
            ? 'cursor-pointer hover:bg-muted/40 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-ring'
            : 'cursor-pointer hover:-translate-y-px hover:border-brand/50 hover:bg-card hover:shadow-card-hover focus:outline-none focus:ring-2 focus:ring-brand-ring'
          : ''
      }`}
    >
      <div className={`flex items-center gap-2 ${compact ? 'mb-2' : 'mb-3'}`}>
        <span
          className="flex h-6 w-6 flex-none items-center justify-center rounded-md transition-colors"
          style={{ backgroundColor: 'color-mix(in srgb, var(--muted-foreground) 12%, transparent)' }}
        >
          <Icon className="h-3.5 w-3.5 text-muted-foreground transition-colors group-hover:text-brand" />
        </span>
        <span className="min-w-0 flex-1 truncate text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {title}
        </span>
        {badge && (
          <span
            className="flex-none rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none"
            style={tint('var(--destructive)')}
          >
            {badge}
          </span>
        )}
        {interactive && (
          <ArrowRightIcon className="h-3 w-3 flex-none text-muted-foreground/40 transition-all group-hover:translate-x-0.5 group-hover:text-brand" />
        )}
      </div>
      {loading ? (
        <div className="flex-1 space-y-2">
          <div className="h-3 w-2/3 animate-pulse rounded bg-muted" />
          <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
          <div className="h-3 w-3/5 animate-pulse rounded bg-muted" />
        </div>
      ) : (
        /**
         * The body owns the card's slack, and SPREADS it.
         *
         * Cards in a row are the same height — a grid stretches them — but their content is not
         * the same height, so somewhere there is space left over. Left alone it all pooled at the
         * bottom, which reads as a card with something missing rather than as a card with room.
         * `justify-between` distributes it between the blocks instead: the headline stays under
         * the header, the footer sits on the floor, and the gap lands in the middle where it
         * reads as breathing room. The header is outside this box so it can't drift down with it.
         */
        <div className="flex flex-1 flex-col justify-between gap-2">{children}</div>
      )}
    </div>
  );
}

/**
 * The tile's headline figure. Gives the sparse tiles (recording, models, groups) something to
 * scan at the same visual weight as the donut, so no tile reads as filler. `tone` tints only the
 * small dot, never the number — see `Row`.
 */
function Hero({ value, caption, tone }: { value: number; caption: string; tone?: string }) {
  return (
    <div className="flex items-baseline gap-1.5">
      {tone && value > 0 && (
        <span className="mb-1 h-2 w-2 flex-none self-end rounded-full" style={{ backgroundColor: tone }} aria-hidden />
      )}
      <span className="text-[26px] font-bold leading-none tracking-tight text-foreground">{value}</span>
      <span className="min-w-0 truncate text-[10px] uppercase tracking-wider text-muted-foreground">{caption}</span>
    </div>
  );
}

/**
 * Two figures at equal weight, for a tile where neither number is subordinate to the other. Same
 * numeral size as `Hero`, with the caption beneath instead of inline so two of them fit.
 *
 * The status dot renders even at 0, unlike `Hero` — the two columns must stay structurally
 * identical or "equal weight" breaks the moment one hits zero.
 */
function HeroPair({ items }: { items: { value: number; label: string; tone?: string }[] }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {items.map((it) => (
        <div key={it.label} className="min-w-0">
          <div className="flex items-baseline gap-1.5">
            {it.tone && (
              <span
                className="mb-1 h-2 w-2 flex-none self-end rounded-full"
                style={{ backgroundColor: it.tone }}
                aria-hidden
              />
            )}
            <span className="text-[26px] font-bold leading-none tracking-tight text-foreground">{it.value}</span>
          </div>
          <p className="mt-1 truncate text-[10px] uppercase tracking-wider text-muted-foreground">{it.label}</p>
        </div>
      ))}
    </div>
  );
}

/**
 * Label + value line. `tone` puts a status dot BEFORE the label rather than colouring the
 * number: values stay in foreground ink so they're legible at any status, and the dot carries
 * the state — the same split the donut legend uses.
 */
function Row({
  label,
  value,
  tone,
  icon: Icon,
}: {
  label: string;
  value: string;
  tone?: string;
  icon?: ComponentType<{ className?: string }>;
}) {
  return (
    <div className="flex items-center gap-1.5 text-[11px]">
      {tone && <span className="h-2 w-2 flex-none rounded-full" style={{ backgroundColor: tone }} aria-hidden />}
      {Icon && <Icon className="h-3 w-3 flex-none text-muted-foreground" />}
      <span className="min-w-0 flex-1 truncate text-muted-foreground">{label}</span>
      <span className="flex-none font-semibold tabular-nums text-foreground">{value}</span>
    </div>
  );
}

/**
 * A single ratio against a limit is a meter, not a chart — one track, one fill, value stated in
 * text so the bar is never the only channel.
 */
function Meter({ label, percent, hint }: { label: string; percent: number; hint?: string }) {
  const clamped = Math.max(0, Math.min(100, percent));
  // Load thresholds reuse the reserved status tokens, so a hot node reads the same way here as
  // it does on the monitor page.
  const color = clamped >= 90 ? 'var(--destructive)' : clamped >= 75 ? 'var(--warning)' : 'var(--success)';
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[11px]">
        <span className="min-w-0 flex-1 truncate text-muted-foreground">{label}</span>
        {hint && <span className="flex-none text-[10px] tabular-nums text-muted-foreground/70">{hint}</span>}
        <span className="flex-none font-semibold tabular-nums text-foreground">
          {clamped ? `${Math.round(clamped)}%` : '—'}
        </span>
      </div>
      {/* Track is a mix off the ink colour, not `bg-muted`: muted against a recessed
          `bg-background` tile is nearly invisible in light mode. */}
      <div
        className="mt-1 h-1.5 overflow-hidden rounded-full"
        style={{ backgroundColor: 'color-mix(in srgb, var(--muted-foreground) 20%, transparent)' }}
      >
        <div className="h-full rounded-full transition-all" style={{ width: `${clamped}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

/* ─── Formatting ─── */

const num = (v: any) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const gb = (mb: any) => `${(num(mb) / 1024).toFixed(1)}GB`;
const pct = (used: any, total: any) => (num(total) > 0 ? (num(used) / num(total)) * 100 : 0);

function severityColor(severity?: string): string {
  switch ((severity || '').toLowerCase()) {
    case 'critical': return 'var(--destructive)';
    case 'warning': return 'var(--warning)';
    default: return 'var(--info)';
  }
}

export function formatAlertTime(al: any): string {
  // CC alerts carry `event_timestamp` in epoch SECONDS; `created_at` is an ISO string.
  const ms = al.event_timestamp ? Number(al.event_timestamp) * 1000 : Date.parse(al.created_at || '');
  if (!Number.isFinite(ms)) return '—';
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
  return `${Math.floor(diff / 86_400_000)}d`;
}

export function formatUptime(seconds: any): string {
  const s = num(seconds);
  if (s <= 0) return '—';
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${mins}m`;
  return `${mins}m`;
}
